import tt from '@tomtom-international/web-sdk-maps';
import '@tomtom-international/web-sdk-maps/dist/maps.css';
import { AutoComplete, Button, Col, Input, Row } from 'antd';
import { Loading } from 'app/components/Loading';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';

const NewLocationMap = ({
  apiKey,
  onLocationClick,
  initialLatitude,
  initialLongitude,
  radius,
}) => {
  const mapElement = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onLocationClickRef = useRef(onLocationClick);
  onLocationClickRef.current = onLocationClick;
  const [selectedLocation, setSelectedLocation] = useState({
    lng: initialLongitude,
    lat: initialLatitude,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const drawCircle = async () => {
      // Only draw if we have valid coordinates
      if (!selectedLocation.lat || !selectedLocation.lng) return;

      const circle = createCircle(
        [Number(selectedLocation.lng), Number(selectedLocation.lat)],
        radius ?? 200,
      );

      if (map.getSource('circle')) {
        map.getSource('circle').setData(circle);
      } else {
        console.log('no circle there');
        map.addSource('circle', { type: 'geojson', data: circle });
        map.addLayer({
          id: 'circle-fill',
          type: 'fill',
          source: 'circle',
          paint: { 'fill-color': '#007aff', 'fill-opacity': 0.3 },
        });
        map.addLayer({
          id: 'circle-outline',
          type: 'line',
          source: 'circle',
          paint: { 'line-color': '#007aff', 'line-width': 2 },
        });
      }
    };

    // If map is already loaded and selectedLocation exists, draw immediately
    if (map.loaded() && selectedLocation.lat && selectedLocation.lng) {
      drawCircle();
    } else {
      // Otherwise, wait for map load
      const onLoad = () => drawCircle();
      map.on('load', () => {
        onLoad();
      });

      return () => {
        map.off('load', onLoad);
      };
    }
  }, [selectedLocation.lat, selectedLocation.lng, radius]);

  useEffect(() => {
    if (!apiKey || !mapElement.current) return;

    const kuwaitCity = [47.9774, 29.3759]; // [lng, lat]

    const mapInstance = tt.map({
      key: apiKey,
      container: mapElement.current,
      center: kuwaitCity,
      zoom: 15,
    });

    if (initialLatitude && initialLongitude) {
      const initialMarker = new tt.Marker()
        .setLngLat([initialLongitude, initialLatitude])
        .addTo(mapInstance);

      mapInstance.flyTo({
        center: [initialLongitude, initialLatitude],
        zoom: 15,
        speed: 1.2, // animation speed
      });

      markerRef.current = initialMarker;
    }

    mapInstance.on('load', () => {
      setLoading(false);
    });
    mapRef.current = mapInstance;

    // Allow manual click to place marker
    mapInstance.on('click', e => {
      const { lng, lat } = e.lngLat;
      if (markerRef.current) markerRef.current.remove();

      const newMarker = new tt.Marker()
        .setLngLat([lng, lat])
        .addTo(mapInstance);
      markerRef.current = newMarker;
      setSelectedLocation({ lng, lat });
      onLocationClickRef.current?.({ lng, lat });
    });

    // return () => mapInstance.remove();
  }, [apiKey, initialLatitude, initialLongitude]);

  // Debounced function for typeahead suggestions
  const fetchSuggestions = debounce(async query => {
    if (!query) return;
    try {
      const response = await fetch(
        `https://api.tomtom.com/search/2/search/${encodeURIComponent(
          query,
        )}.json?key=${apiKey}&limit=5&countrySet=KW&typeahead=true`,
      );
      const data = await response.json();

      const newOptions =
        data.results?.map(r => {
          const name = r.poi?.name || '';
          const address = r.address?.freeformAddress || '';
          const label =
            name && address ? `${name} — ${address}` : name || address;

          return {
            value: label, // What shows in dropdown
            label: label, // Explicitly set label for AutoComplete
            position: r.position,
          };
        }) || [];

      setOptions(newOptions);
    } catch (err) {
      console.error('Autocomplete error:', err);
    }
  }, 400);

  // When user clicks a suggestion
  const handleSelect = value => {
    setSearchQuery(value); // ✅ update input value
    const item = options.find(o => o.value === value);
    if (item?.position) {
      const { lat, lon } = item.position;
      placeMarkerAndFly(lat, lon);
      onLocationClickRef.current?.({ lng: lon, lat });
    }
  };

  // When user presses Enter without clicking suggestion
  const handleSearchSubmit = async () => {
    if (!searchQuery) return;
    try {
      const response = await fetch(
        `https://api.tomtom.com/search/2/geocode/${encodeURIComponent(
          searchQuery,
        )}.json?key=${apiKey}&limit=1&countrySet=KW`,
      );
      const data = await response.json();
      const firstResult = data.results?.[0];
      if (firstResult?.position) {
        const { lat, lon } = firstResult.position;
        placeMarkerAndFly(lat, lon);
      } else {
        alert('No results found for this query.');
      }
    } catch (err) {
      console.error('Search submit error:', err);
    }
  };

  // Utility function to drop a single marker & move map
  const placeMarkerAndFly = (lat, lon) => {
    if (markerRef.current) markerRef.current.remove();
    const newMarker = new tt.Marker()
      .setLngLat([lon, lat])
      .addTo(mapRef.current);
    markerRef.current = newMarker;
    setSelectedLocation({ lng: lon, lat });
    mapRef.current.flyTo({ center: [lon, lat], zoom: 15 });
  };

  const handleSubmit = () => {
    if (!selectedLocation) {
      alert('Please select a location first!');
      return;
    }
  };

  return (
    <div style={{ position: 'relative' }} className="my-4">
      <Row gutter={[8, 8]} className="mt-4">
        <div ref={mapElement} style={{ height: 500, width: '100%' }} />
      </Row>

      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1000,
          }}
        >
          <Loading />
        </div>
      )}

      <Row gutter={[8, 8]} className="mt-4">
        <Col md={20} xs={24}>
          <AutoComplete
            style={{ width: '100%' }}
            options={options}
            value={searchQuery}
            onSelect={handleSelect}
            onSearch={value => {
              setSearchQuery(value);
              fetchSuggestions(value);
            }}
          >
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onPressEnter={handleSearchSubmit}
              placeholder="Search Location ..."
            />
          </AutoComplete>
        </Col>

        <Col md={4} xs={24}>
          <Button
            type="primary"
            onClick={handleSubmit}
            style={{ width: '100%' }}
          >
            Submit Location
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default NewLocationMap;

function createCircle(center, radiusInMeters, points = 64) {
  const coords = {
    latitude: center[1],
    longitude: center[0],
  };

  const km = radiusInMeters / 1000;
  const ret = [];
  const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
  const distanceY = km / 110.574;

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);

    ret.push([coords.longitude + x, coords.latitude + y]);
  }
  ret.push(ret[0]);

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [ret],
    },
  };
}
